/**
 * Mesh Gateway - Bridge between mesh network and internet
 * Allows offline users to make API requests through online gateway users
 */

import { saveData, getAllData, STORES } from './offlineStorage';

const GATEWAY_TIMEOUT = 45000;
const CHUNK_SIZE = 200;
const MAX_RETRIES = 3;

const MESSAGE_TYPES = {
  GATEWAY_ANNOUNCE: 'gateway_announce',
  GATEWAY_REQUEST: 'gateway_request',
  GATEWAY_RESPONSE: 'gateway_response',
  GATEWAY_CHUNK: 'gateway_chunk',
  GATEWAY_ACK: 'gateway_ack',
  GATEWAY_ERROR: 'gateway_error'
};

class MeshGateway {
  constructor() {
    this.isGateway = false;
    this.hasInternet = navigator.onLine;
    this.availableGateways = new Map();
    this.pendingRequests = new Map();
    this.meshConnection = null;
    this.debug = false; // Set to true for development logging
    
    window.addEventListener('online', () => {
      this.hasInternet = true;
      if (this.isGateway) this.announceGateway();
    });
    
    window.addEventListener('offline', () => {
      this.hasInternet = false;
    });
  }

  log(...args) {
    if (this.debug) console.log('[MeshGateway]', ...args);
  }

  async initialize(meshConnection) {
    this.meshConnection = meshConnection;
    
    if (meshConnection) {
      meshConnection.on('message', (msg) => this.handleMeshMessage(msg));
    }
    
    if (this.hasInternet) {
      this.enableGateway();
    }
    
    await this.discoverGateways();
  }

  enableGateway() {
    this.isGateway = true;
    this.announceGateway();
    this.log('Gateway enabled');
  }

  disableGateway() {
    this.isGateway = false;
    this.log('Gateway disabled');
  }

  announceGateway() {
    if (!this.meshConnection || !this.hasInternet) return;
    
    this.sendMeshMessage({
      type: MESSAGE_TYPES.GATEWAY_ANNOUNCE,
      gatewayId: this.getDeviceId(),
      timestamp: Date.now(),
      capabilities: ['http', 'https', 'api']
    });
  }

  async discoverGateways() {
    if (this.meshConnection) {
      this.sendMeshMessage({
        type: 'gateway_discovery',
        requesterId: this.getDeviceId(),
        timestamp: Date.now()
      });
    }
    await new Promise(resolve => setTimeout(resolve, 2000));
    this.log(`Found ${this.availableGateways.size} gateway(s)`);
  }

  async sendThroughGateway(url, options = {}) {
    if (this.hasInternet) {
      return fetch(url, options);
    }
    
    const gateway = this.selectBestGateway();
    if (!gateway) {
      throw new Error('No mesh gateways available. Try offline mode.');
    }
    
    const requestId = this.generateRequestId();
    const requestData = await this.packageRequest(url, options);
    
    await this.sendChunkedData(gateway.gatewayId, requestId, requestData);
    return this.waitForGatewayResponse(requestId, gateway.gatewayId);
  }

  async packageRequest(url, options) {
    const pkg = {
      url,
      method: options.method || 'GET',
      headers: options.headers || {},
      timestamp: Date.now()
    };
    
    if (options.body instanceof FormData) {
      const formObj = {};
      for (let [key, value] of options.body.entries()) {
        if (value instanceof File) {
          formObj[key] = {
            type: 'file',
            name: value.name,
            mimeType: value.type,
            data: await this.fileToBase64(value)
          };
        } else {
          formObj[key] = value;
        }
      }
      pkg.body = formObj;
      pkg.bodyType = 'formdata';
    } else if (options.body) {
      pkg.body = options.body;
      pkg.bodyType = 'raw';
    }
    
    return JSON.stringify(pkg);
  }

  async sendChunkedData(gatewayId, requestId, data) {
    const chunks = this.chunkData(data);
    
    for (let i = 0; i < chunks.length; i++) {
      this.sendMeshMessage({
        type: MESSAGE_TYPES.GATEWAY_CHUNK,
        requestId,
        gatewayId,
        chunkIndex: i,
        totalChunks: chunks.length,
        data: chunks[i],
        timestamp: Date.now()
      });
      
      const ackReceived = await this.waitForAck(requestId, i);
      if (!ackReceived) {
        throw new Error(`Failed to send chunk ${i}/${chunks.length}`);
      }
      
      await new Promise(resolve => setTimeout(resolve, 100));
    }
  }

  chunkData(data) {
    const chunks = [];
    for (let i = 0; i < data.length; i += CHUNK_SIZE) {
      chunks.push(data.substring(i, i + CHUNK_SIZE));
    }
    return chunks;
  }

  async waitForAck(requestId, chunkIndex) {
    return new Promise((resolve) => {
      const ackKey = `${requestId}_${chunkIndex}`;
      let attempts = 0;
      
      const checkAck = setInterval(() => {
        if (this.pendingRequests.has(ackKey)) {
          clearInterval(checkAck);
          this.pendingRequests.delete(ackKey);
          resolve(true);
        } else if (++attempts > MAX_RETRIES) {
          clearInterval(checkAck);
          resolve(false);
        }
      }, 500);
    });
  }

  async waitForGatewayResponse(requestId, gatewayId) {
    return new Promise((resolve, reject) => {
      const timeout = setTimeout(() => {
        this.pendingRequests.delete(requestId);
        reject(new Error('Gateway request timeout'));
      }, GATEWAY_TIMEOUT);
      
      this.pendingRequests.set(requestId, {
        resolve: (data) => {
          clearTimeout(timeout);
          resolve(data);
        },
        reject: (error) => {
          clearTimeout(timeout);
          reject(error);
        },
        chunks: [],
        expectedChunks: null
      });
    });
  }

  async handleMeshMessage(message) {
    const { type, data } = message;
    
    switch (type) {
      case MESSAGE_TYPES.GATEWAY_ANNOUNCE:
        this.handleGatewayAnnounce(data);
        break;
      case MESSAGE_TYPES.GATEWAY_REQUEST:
        if (this.isGateway && this.hasInternet) {
          await this.handleGatewayRequest(data);
        }
        break;
      case MESSAGE_TYPES.GATEWAY_CHUNK:
        await this.handleChunk(data);
        break;
      case MESSAGE_TYPES.GATEWAY_RESPONSE:
        this.handleGatewayResponse(data);
        break;
      case MESSAGE_TYPES.GATEWAY_ACK:
        this.handleAck(data);
        break;
      case MESSAGE_TYPES.GATEWAY_ERROR:
        this.handleGatewayError(data);
        break;
    }
  }

  handleGatewayAnnounce(data) {
    this.availableGateways.set(data.gatewayId, {
      gatewayId: data.gatewayId,
      capabilities: data.capabilities,
      lastSeen: Date.now(),
      latency: 0
    });
  }

  async handleGatewayRequest(data) {
    try {
      const request = JSON.parse(data.requestData);
      
      const options = {
        method: request.method,
        headers: request.headers
      };
      
      if (request.bodyType === 'formdata') {
        const formData = new FormData();
        for (let [key, value] of Object.entries(request.body)) {
          if (value.type === 'file') {
            const blob = this.base64ToBlob(value.data, value.mimeType);
            formData.append(key, blob, value.name);
          } else {
            formData.append(key, value);
          }
        }
        options.body = formData;
      } else if (request.body) {
        options.body = request.body;
      }
      
      const response = await fetch(request.url, options);
      const responseData = await response.json();
      
      await this.sendGatewayResponse(data.requesterId, data.requestId, {
        status: response.status,
        statusText: response.statusText,
        data: responseData
      });
      
    } catch (error) {
      this.sendGatewayError(data.requesterId, data.requestId, error.message);
    }
  }

  async sendGatewayResponse(requesterId, requestId, responseData) {
    const responseStr = JSON.stringify(responseData);
    const chunks = this.chunkData(responseStr);
    
    for (let i = 0; i < chunks.length; i++) {
      this.sendMeshMessage({
        type: MESSAGE_TYPES.GATEWAY_RESPONSE,
        requestId,
        requesterId,
        chunkIndex: i,
        totalChunks: chunks.length,
        data: chunks[i]
      });
      await new Promise(resolve => setTimeout(resolve, 100));
    }
  }

  async handleChunk(data) {
    const pending = this.pendingRequests.get(data.requestId);
    if (!pending) return;
    
    this.sendMeshMessage({
      type: MESSAGE_TYPES.GATEWAY_ACK,
      requestId: data.requestId,
      chunkIndex: data.chunkIndex
    });
    
    pending.chunks[data.chunkIndex] = data.data;
    pending.expectedChunks = data.totalChunks;
    
    if (pending.chunks.filter(c => c).length === data.totalChunks) {
      const fullData = pending.chunks.join('');
      const parsed = JSON.parse(fullData);
      
      pending.resolve({
        ok: parsed.status >= 200 && parsed.status < 300,
        status: parsed.status,
        statusText: parsed.statusText,
        json: async () => parsed.data
      });
      
      this.pendingRequests.delete(data.requestId);
    }
  }

  handleGatewayResponse(data) {
    // Handled by handleChunk
  }

  handleAck(data) {
    const ackKey = `${data.requestId}_${data.chunkIndex}`;
    this.pendingRequests.set(ackKey, true);
  }

  handleGatewayError(data) {
    const pending = this.pendingRequests.get(data.requestId);
    if (pending) {
      pending.reject(new Error(data.error));
      this.pendingRequests.delete(data.requestId);
    }
  }

  sendGatewayError(requesterId, requestId, error) {
    this.sendMeshMessage({
      type: MESSAGE_TYPES.GATEWAY_ERROR,
      requestId,
      requesterId,
      error
    });
  }

  selectBestGateway() {
    if (this.availableGateways.size === 0) return null;
    return Array.from(this.availableGateways.values())[0];
  }

  sendMeshMessage(message) {
    if (!this.meshConnection) return;
    
    if (this.meshConnection.send) {
      this.meshConnection.send(JSON.stringify(message));
    }
  }

  getDeviceId() {
    let deviceId = localStorage.getItem('mesh_device_id');
    if (!deviceId) {
      deviceId = 'device_' + Math.random().toString(36).substr(2, 9);
      localStorage.setItem('mesh_device_id', deviceId);
    }
    return deviceId;
  }

  generateRequestId() {
    return `req_${Date.now()}_${Math.random().toString(36).substr(2, 9)}`;
  }

  async fileToBase64(file) {
    return new Promise((resolve, reject) => {
      const reader = new FileReader();
      reader.readAsDataURL(file);
      reader.onload = () => resolve(reader.result);
      reader.onerror = reject;
    });
  }

  base64ToBlob(base64, mimeType) {
    const byteString = atob(base64.split(',')[1]);
    const ab = new ArrayBuffer(byteString.length);
    const ia = new Uint8Array(ab);
    for (let i = 0; i < byteString.length; i++) {
      ia[i] = byteString.charCodeAt(i);
    }
    return new Blob([ab], { type: mimeType });
  }
}

export const meshGateway = new MeshGateway();

export async function gatewayFetch(url, options = {}) {
  return meshGateway.sendThroughGateway(url, options);
}

export default meshGateway;
